'use client';

import { MapPin, SlidersHorizontal, Zap, Unlock, ArrowRight } from 'lucide-react';

const NeuralBackground = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
    <svg className="absolute w-full h-full opacity-40" viewBox="0 0 1440 800" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
      {/* Neural Pathways (Curved lines mimicking synapses) */}
      <path d="M-100 200 C 200 350, 400 150, 600 400 S 1000 650, 1500 300" stroke="url(#greenBlueGradient)" strokeWidth="1.5" strokeDasharray="4 6" />
      <path d="M-50 600 C 300 450, 500 850, 800 400 S 1200 150, 1600 500" stroke="url(#greenBlueGradient)" strokeWidth="1.5" strokeDasharray="6 8" />
      <path d="M200 -100 C 350 250, 100 650, 500 750 S 950 250, 1200 -50" stroke="url(#greenBlueGradient)" strokeWidth="1" strokeDasharray="8 4" />
      <path d="M400 900 C 550 550, 750 750, 950 400 S 1150 50, 1400 250" stroke="url(#greenBlueGradient)" strokeWidth="1" />

      {/* Interconnecting synapses (Straight intersecting lines) */}
      <line x1="600" y1="400" x2="800" y2="400" stroke="url(#greenBlueGradient)" strokeWidth="1" className="opacity-60" />
      <line x1="500" y1="750" x2="600" y2="400" stroke="url(#greenBlueGradient)" strokeWidth="1" className="opacity-50" />
      <line x1="800" y1="400" x2="950" y2="400" stroke="url(#greenBlueGradient)" strokeWidth="1" className="opacity-60" />

      {/* Neural Nodes (Glowing dots) */}
      <circle cx="600" cy="400" r="5" fill="#10b981" className="animate-pulse" />
      <circle cx="800" cy="400" r="4" fill="#3b82f6" className="animate-pulse" style={{ animationDelay: '1s' }} />
      <circle cx="500" cy="750" r="6" fill="#10b981" className="animate-pulse" style={{ animationDelay: '0.5s' }} />
      <circle cx="950" cy="400" r="5" fill="#3b82f6" className="animate-pulse" style={{ animationDelay: '1.5s' }} />
      <circle cx="280" cy="275" r="3" fill="#10b981" className="animate-pulse" style={{ animationDelay: '0.2s' }} />
      <circle cx="1180" cy="215" r="4" fill="#10b981" className="animate-pulse" style={{ animationDelay: '0.8s' }} />

      <defs>
        <linearGradient id="greenBlueGradient" x1="0" y1="0" x2="1440" y2="800" gradientUnits="userSpaceOnUse">
          <stop stopColor="#10b981" stopOpacity="0.8" />
          <stop offset="1" stopColor="#3b82f6" stopOpacity="0.4" />
        </linearGradient>
      </defs>
    </svg>

    {/* Abstract glowing orbs behind the network */}
    <div className="absolute top-[10%] left-[20%] w-[40rem] h-[40rem] bg-green-500 rounded-full blur-[140px] opacity-[0.05] mix-blend-multiply pointer-events-none"></div>
    <div className="absolute bottom-[10%] right-[20%] w-[40rem] h-[40rem] bg-blue-500 rounded-full blur-[140px] opacity-[0.05] mix-blend-multiply pointer-events-none"></div>
  </div>
);

export default function BenefitsSection() {
  const benefits = [
    {
      icon: <MapPin className="w-8 h-8 text-blue-600" strokeWidth={1.5} />,
      bgIcon: "bg-blue-100",
      title: 'Presença Nacional',
      description: 'Acesso imediato a milhares de clínicas credenciadas em todo o país.',
    },
    {
      icon: <SlidersHorizontal className="w-8 h-8 text-purple-600" strokeWidth={1.5} />,
      bgIcon: "bg-purple-100",
      title: 'Gestão Sob Medida',
      description: 'Ajuste e parametrização detalhada das regras de exames por função.',
    },
    {
      icon: <Zap className="w-8 h-8 text-amber-600" strokeWidth={1.5} />,
      bgIcon: "bg-amber-100",
      title: 'Emissão Rápida',
      description: 'Zero filas e burocracias. Colaboradores faturados de forma prévia.',
    },
    {
      icon: <Unlock className="w-8 h-8 text-green-600" strokeWidth={1.5} />,
      bgIcon: "bg-green-100",
      title: 'Autonomia Plena',
      description: 'O seu RH agenda, acompanha e gerencia os atestados integralmente.',
    }
  ]

  return (
    <section className="py-24 bg-white relative overflow-hidden">

      <NeuralBackground />

      <div className="container mx-auto px-4 relative z-10">

        <div className="text-center mb-16 max-w-4xl mx-auto">
          <h3 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-6 tracking-tight">
            O Ecossistema Completo de <span className="text-green-600">SST</span>
          </h3>
          <p className="text-xl text-gray-500 leading-relaxed">
            Esqueça softwares fragmentados e trabalho manual. Nossa plataforma unifica a sua operação em uma jornada contínua: desde a escolha da clínica até o controle em tempo real dos exames.
          </p>
        </div>

        {/* Removed the 'white card' background completely, allowing the neural background to bleed through */}
        <div className="max-w-7xl mx-auto py-8 relative">

          <div className="relative z-10">
            <div className="text-center mb-20">
              <h4 className="text-2xl font-semibold text-gray-900">Jornada Inteligente de Agendamento</h4>
              <p className="text-gray-500 mt-2">Um fluxo de trabalho neural onde cada etapa engatilha a próxima.</p>
            </div>

            {/* Timeline Flow */}
            <div className="relative">
              {/* Linha de ligação gradiente (apenas Desktop) - Mimetizando uma conexão neural forte */}
              <div className="hidden lg:block absolute top-[3.5rem] left-[12%] w-[76%] h-[2px] bg-gradient-to-r from-blue-300 via-purple-300 to-green-400 opacity-60 z-0"></div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16 lg:gap-8">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex flex-col items-center text-center group relative p-6 lg:p-0">

                    {/* Número Passos estilo Synapse Bubble */}
                    <div className="absolute -top-4 -left-4 lg:top-auto lg:left-auto lg:-top-6 lg:-right-2 w-8 h-8 rounded-full bg-white shadow-md border border-gray-100 text-gray-400 font-bold flex items-center justify-center text-sm z-20 group-hover:text-green-500 group-hover:border-green-300 transition-colors">
                      {index + 1}
                    </div>

                    {/* Ícone Container */}
                    <div className="relative mb-6 z-10 bg-white p-2 rounded-full shadow-[0_4px_20px_rgb(0,0,0,0.06)] group-hover:shadow-[0_4px_30px_rgb(16,185,129,0.15)] transition-shadow duration-500">
                      <div className={`w-24 h-24 ${benefit.bgIcon} rounded-full flex items-center justify-center shadow-inner group-hover:scale-[1.05] transition-all duration-300`}>
                        {benefit.icon}
                      </div>

                      {/* Seta conectora flutuante (Desktop) */}
                      {index < benefits.length - 1 && (
                        <div className="hidden lg:block absolute top-1/2 -right-8 -translate-y-1/2 text-gray-300 group-hover:text-green-500 group-hover:translate-x-3 transition-all duration-300 z-0 drop-shadow-sm">
                          <ArrowRight className="w-8 h-8" />
                        </div>
                      )}
                    </div>

                    <h5 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-green-600 transition-colors bg-white/60 px-4 py-1 rounded-xl backdrop-blur-md">
                      {benefit.title}
                    </h5>

                    <p className="text-base text-gray-500 leading-relaxed px-2 bg-white/60 py-2 rounded-xl backdrop-blur-md">
                      {benefit.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>

      </div>
    </section>
  )
}
